import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import {
  LeadActivityUiModel,
  LeadCommentUiModel,
  LeadContactAttemptUiModel,
  LeadUiModel
} from '../../../shared/ui-models/lead.ui-model';
import { LeadConfigModel } from '../../../shared/models/lead.model';
import {
  addLeadComment,
  assignLead,
  changeLeadStatus,
  getLeadById,
  getLeadComments,
  getLeadConfig,
  getLeadTimeline,
  recordContactAttempt,
  updateLead
} from '../../../shared/services/data/lead.data';
import { APP_ROUTES } from '../../../shared/config/routes-config';
import { RouteConfig } from '../../../shared/interfaces/routes-config.interface';
import Button, { ButtonType } from '../../../shared/components/core-ui/button/button.component';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { TeamMemberPicker } from '../../../shared/components/group-ui/team-member-picker/team-member-picker.component';
import { LoadingData } from '../../../ui/LoadingData';
import { NotFoundData } from '../../../ui/NotFoundData';
import { SlaBadge } from '../../shared/sla-badge.component';
import {
  CHIP,
  resolutionStyle,
  STATUS_STYLE,
  VERTICAL_STYLE,
  styleFor
} from '../../shared/lead-visuals';
import { detailFieldFor } from '../../shared/lead-detail-fields.config';
import { LeadFormModal } from '../../shared/lead-form-modal.component';
import { StatusModal } from './components/status-modal.component';
import {
  Card,
  Field,
  FieldList,
  FieldSpec,
  LABEL_CLS,
  SummaryCell,
  SummaryStrip
} from './components/lead-detail-ui';

const LABEL = 'mb-1 block text-base font-gilroy-medium text-neutral-400';

const titleCase = (value: string): string =>
  (value || '').replace(/_/g, ' ').replace(/^./, (c: string) => c.toUpperCase());

const formatDate = (value?: Date): string | undefined =>
  value ? new Date(value).toLocaleString() : undefined;

/**
 * Detail keys holding money. Rendered grouped, because a furnishing quote runs
 * to six figures and `250000` is genuinely hard to read at a glance.
 */
const MONEY_KEYS = ['budget', 'quotedPrice', 'expectedMonthlyRent', 'budgetPerNight'];

/**
 * Catalogue ids the BFF resolves to names. The id is what every write path
 * takes, so it stays — but it is shown as a footnote, not as the answer.
 *
 * `areaName`, `unitTypeGroup` and `typeOfUnit` are here because the same key
 * holds either an id or a name depending on which door the lead came through:
 * the admin form posts names, website intake posts ids. The BFF returns the
 * resolved value for both cases, so the footnote is suppressed when it would
 * only repeat what is already shown.
 */
const ID_FOOTNOTE_KEYS = ['packageId', 'modelId'];

const RESOLVED_KEYS = [
  'packageId',
  'modelId',
  'unitTypeGroup',
  'typeOfUnit',
  'propertyType',
  'areaName',
  'addons'
];

/**
 * A stored detail value as text.
 *
 * Dates arrive as ISO strings inside `details` — the sub-document is a loose
 * object, so nothing converts them the way the mapper converts the lead's own
 * timestamps.
 */
function formatDetail(value: unknown, key?: string): string | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  if (Array.isArray(value)) return value.length ? value.join(', ') : undefined;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return new Date(value).toLocaleDateString();
  }
  if (key && MONEY_KEYS.includes(key) && typeof value === 'number') {
    return `EGP ${value.toLocaleString()}`;
  }
  return String(value);
}

/**
 * A note on the lead, whatever produced it.
 *
 * The resolution note was written into `lead.resolutionNote` and rendered
 * nowhere — someone typed why a lead was closed and it vanished. It is not a
 * comment (it belongs to the resolution, not to a conversation), so rather
 * than writing it into the comment collection it is merged here for reading
 * and labelled with where it came from.
 */
interface NoteEntry {
  id: string;
  kind: 'comment' | 'resolution';
  text: string;
  author?: string;
  at?: Date;
  /** Resolution notes carry the outcome they explain; a comment has none. */
  context?: string;
}

const NOTE_LABEL: Record<NoteEntry['kind'], { label: string; chip: string }> = {
  comment: { label: 'Comment', chip: 'bg-creams-400 text-creams-900' },
  resolution: { label: 'Resolution note', chip: 'bg-primary-100 text-primary-800' }
};

export const LeadDetailsContainer: React.FC = () => {
  const { leadId } = useParams<{ leadId: string }>();
  const history = useHistory();
  const [lead, setLead] = useState<LeadUiModel | undefined>();
  const [timeline, setTimeline] = useState<LeadActivityUiModel[]>([]);
  const [comments, setComments] = useState<LeadCommentUiModel[]>([]);
  const [config, setConfig] = useState<LeadConfigModel | undefined>();
  const [loading, setLoading] = useState(true);
  const [showStatus, setShowStatus] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [draftComment, setDraftComment] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([
      getLeadById(leadId),
      getLeadTimeline(leadId),
      getLeadComments(leadId),
      getLeadConfig()
    ]).then(([leadResult, timelineResult, commentsResult, configResult]) => {
      if (!isErrorInfo(leadResult)) setLead(leadResult);
      if (!isErrorInfo(timelineResult)) setTimeline(timelineResult);
      if (!isErrorInfo(commentsResult)) setComments(commentsResult);
      if (!isErrorInfo(configResult)) setConfig(configResult);
      setLoading(false);
    });
  }, [leadId]);

  useEffect(() => {
    load();
  }, [load]);

  const run = (
    promise: Promise<unknown>,
    messages: { loading: string; success: string; failed: string }
  ) => {
    const wrapped = promise.then((response: unknown) => {
      if (isErrorInfo(response as ErrorInfo)) throw response;
      return load();
    });
    birdToast.promise(wrapped, {
      loadingMessage: messages.loading,
      successMessage: messages.success,
      errorPermissionMessage: 'You do not have permission to change this lead',
      errorFailedMessage: messages.failed
    });
  };

  const handleStatus = (payload: Record<string, unknown>) => {
    setShowStatus(false);
    run(changeLeadStatus(leadId, payload), {
      loading: 'Updating lead...',
      success: 'Lead updated',
      failed: 'Failed to update lead'
    });
  };

  const handleEdit = (changes: Record<string, unknown>) => {
    setShowEdit(false);
    if (Object.keys(changes).length === 0) return;
    run(updateLead(leadId, changes), {
      loading: 'Saving lead...',
      success: 'Lead saved',
      failed: 'Failed to save the lead'
    });
  };

  const handleAttempt = () =>
    run(recordContactAttempt(leadId), {
      loading: 'Recording attempt...',
      success: 'Attempt recorded',
      // Spending the last attempt auto-resolves the lead as unreachable, and
      // any further attempt is refused until someone reopens it.
      failed: 'Could not record — the lead may be resolved already'
    });

  const handleAssign = (userId?: string) => {
    if (!userId) return;
    run(assignLead(leadId, userId), {
      loading: 'Assigning...',
      success: 'Lead assigned',
      failed: 'Failed to assign the lead'
    });
  };

  const handleComment = () => {
    const text = draftComment.trim();
    if (!text) return;
    setDraftComment('');
    run(addLeadComment(leadId, text), {
      loading: 'Adding comment...',
      success: 'Comment added',
      failed: 'Failed to add comment'
    });
  };

  if (loading && !lead) return <LoadingData />;
  if (!lead) return <NotFoundData />;

  const notes: NoteEntry[] = [
    ...comments.map((comment: LeadCommentUiModel) => ({
      id: comment.id,
      kind: 'comment' as const,
      text: comment.text,
      author: comment.authorName,
      at: comment.createdAt
    })),
    ...(lead.resolutionNote
      ? [
          {
            id: `resolution-${lead.id}`,
            kind: 'resolution' as const,
            text: lead.resolutionNote,
            at: lead.resolvedAt,
            // The note on its own does not say what it is explaining.
            context: [lead.resolutionType, lead.notQualifiedReason, lead.notInterestedReason]
              .filter(Boolean)
              .map((value) => titleCase(value as string))
              .join(' · ')
          }
        ]
      : [])
  ].sort((a, b) => new Date(b.at ?? 0).getTime() - new Date(a.at ?? 0).getTime());

  const outOfAttempts = lead.remainingContactAttempts <= 0;
  const listPath = (APP_ROUTES.LEAD_MODULE.CHILDREN as RouteConfig).LIST.FULL_PATH;

  /**
   * The funnel fields, ordered by what this vertical declares rather than by
   * whatever order Mongo returned them in — so the same lead type always reads
   * the same way. Keys the config does not know about are still shown: a lead
   * created before a key was retired still holds it, and hiding it would lose
   * data from the screen rather than from the record.
   */
  const declaredKeys: string[] = config?.detailKeysByVertical?.[lead.vertical] ?? [];
  const storedKeys: string[] = Object.keys(lead.details ?? {});
  const detailKeys: string[] = [
    ...declaredKeys.filter((key: string) => storedKeys.includes(key)),
    ...storedKeys.filter((key: string) => !declaredKeys.includes(key))
  ];

  const attributionFields: FieldSpec[] = [
    {
      label: 'Marketing channel',
      value: lead.marketingChannel && titleCase(lead.marketingChannel)
    },
    {
      label: 'Messaging channel',
      value: lead.messagingChannel && titleCase(lead.messagingChannel)
    },
    { label: 'Ad type', value: lead.adType && titleCase(lead.adType) },
    { label: 'Campaign', value: lead.utmCampaign },
    { label: 'Campaign ID', value: lead.utmCampaignId },
    {
      label: 'Source / medium',
      value: [lead.utmSource, lead.utmMedium].filter(Boolean).join(' / ')
    },
    { label: 'Campaign source', value: lead.campaignSource },
    { label: 'Term', value: lead.utmTerm },
    { label: 'Content', value: lead.utmContent },
    { label: 'Landing page', value: lead.landingPath },
    { label: 'Referrer', value: lead.referrer },
    // Click ids are how a conversion is matched back to an ad. Unreadable by
    // design — nobody reads one, they get copied into a platform's reporting
    // when a campaign's numbers disagree.
    {
      label: 'Click IDs',
      value: [
        lead.gclid ? `gclid ${lead.gclid}` : undefined,
        lead.fbclid ? `fbclid ${lead.fbclid}` : undefined,
        lead.ttclid ? `ttclid ${lead.ttclid}` : undefined
      ]
        .filter(Boolean)
        .join(' · ')
    },
    { label: 'Attributed at', value: formatDate(lead.attributedAt) }
  ];

  const firstTouchFields: FieldSpec[] = [
    { label: 'Channel', value: lead.firstTouchChannel && titleCase(lead.firstTouchChannel) },
    { label: 'Campaign', value: lead.firstTouchCampaign },
    {
      label: 'Source / medium',
      value: [lead.firstTouchSource, lead.firstTouchMedium].filter(Boolean).join(' / ')
    },
    { label: 'Landing page', value: lead.firstTouchLandingPath },
    { label: 'Referrer', value: lead.firstTouchReferrer },
    { label: 'Touched at', value: formatDate(lead.firstTouchAt) }
  ];

  const recordFields: FieldSpec[] = [
    { label: 'Origin', value: lead.originLabel },
    { label: 'Created', value: formatDate(lead.createdAt) },
    { label: 'Created by', value: lead.createdByName },
    { label: 'Last updated', value: formatDate(lead.updatedAt) },
    { label: 'Last updated by', value: lead.lastUpdatedByName },
    { label: 'Resolved', value: formatDate(lead.resolvedAt) },
    { label: 'Pending reason', value: lead.pendingReason && titleCase(lead.pendingReason) },
    {
      label: 'Duplicate of',
      value: lead.duplicateOfLeadId ? (
        <button
          type="button"
          className="text-primary-600 underline"
          onClick={() =>
            history.push(
              (APP_ROUTES.LEAD_MODULE.CHILDREN as RouteConfig).DETAILS.FULL_PATH.replace(
                ':leadId',
                String(lead.duplicateOfLeadId)
              )
            )
          }
        >
          Open the surviving lead
        </button>
      ) : undefined
    }
  ];

  /**
   * What to show for one detail key.
   *
   * `packageId` and `modelId` render as the names the BFF resolved, with the
   * id kept as a footnote — it is what every write path takes, and an agent
   * quoting "12" to an owner is the problem this fixes. The colour lives on
   * the model, which is what sales mean when they ask for it.
   */
  const resolvedName = (key: string): string | undefined => {
    switch (key) {
      case 'packageId':
        return lead.packageName;
      case 'modelId':
        return lead.modelName;
      case 'unitTypeGroup':
        return lead.unitTypeGroupName;
      // Acquisition's name for the same catalogue furnishing calls typeOfUnit.
      case 'typeOfUnit':
      case 'propertyType':
        return lead.typeOfUnitName;
      case 'areaName':
        return lead.areaLabel;
      case 'addons':
        return lead.addonNames?.length ? lead.addonNames.join(', ') : undefined;
      default:
        return undefined;
    }
  };

  const detailValue = (key: string): React.ReactNode => {
    const raw: unknown = (lead.details as Record<string, unknown>)[key];

    if (RESOLVED_KEYS.includes(key)) {
      const name: string | undefined = resolvedName(key);
      const colour: string | undefined = key === 'modelId' ? lead.modelColor : undefined;
      if (!name && !colour) return formatDetail(raw, key);

      const label: string = [name, colour].filter(Boolean).join(' · ');
      /*
        The id footnote is the point for a package or a model — it is the
        number an agent quotes into the furnishing service, so it is worth the
        clutter. Nowhere else: an area's id is a UUID nobody will ever read
        out, and a row of add-on ids under their names is pure noise.
      */
      const footnote: string | undefined =
        ID_FOOTNOTE_KEYS.includes(key) && raw !== undefined && raw !== null
          ? String(raw)
          : undefined;

      return (
        <span>
          {label}
          {footnote ? <span className="ml-2 text-sm text-neutral-300">#{footnote}</span> : null}
        </span>
      );
    }

    return formatDetail(raw, key);
  };

  const attributionSummary: string =
    [lead.marketingChannel && titleCase(lead.marketingChannel), lead.utmCampaign]
      .filter(Boolean)
      .join(' · ') || 'No campaign recorded';

  return (
    <div className="flex h-full flex-col gap-5 md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[2rem]">
      <section className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="text-base text-primary-600"
          onClick={() => history.push(listPath)}
        >
          ← Leads
        </button>
        <h1 className="font-gilroy-bold text-3xl text-neutral-500">
          #{lead.shortId} {lead.name || 'Unnamed lead'}
        </h1>
        <span className={`${CHIP} ${styleFor(VERTICAL_STYLE, lead.vertical)}`}>
          {titleCase(lead.vertical)}
        </span>
        <span className={`${CHIP} ${styleFor(STATUS_STYLE, lead.status)}`}>
          {titleCase(lead.status)}
        </span>
        {lead.resolutionType ? (
          <span
            className={`${CHIP} ${resolutionStyle(
              lead.resolutionType,
              config?.wonResolutionTypes,
              config?.systemOnlyResolutionTypes
            )}`}
          >
            {titleCase(lead.resolutionType)}
          </span>
        ) : null}
        <SlaBadge state={lead.slaState} />
        <span className="ml-auto flex flex-wrap gap-3">
          <Button type={ButtonType.gray} disabled={outOfAttempts} onClick={handleAttempt}>
            {outOfAttempts
              ? 'No attempts left'
              : `Log attempt (${lead.remainingContactAttempts} left)`}
          </Button>
          {/*
            Edit needs the config: the vertical's detail keys come from it, and
            a form opened without them would offer a lead's funnel fields as an
            empty section.
          */}
          <Button type={ButtonType.gray} disabled={!config} onClick={() => setShowEdit(true)}>
            Edit lead
          </Button>
          <Button type={ButtonType.primary} onClick={() => setShowStatus(true)}>
            Change status
          </Button>
        </span>
      </section>

      {/*
        The six values an agent acts on, above everything else and larger than
        everything else. Before this, a phone number sat in a card halfway down
        the page at the same size as a campaign id.
      */}
      <SummaryStrip>
        <SummaryCell
          label="Phone"
          value={lead.phone}
          href={lead.phone ? `tel:${lead.phone}` : undefined}
          emphasis
        />
        <SummaryCell
          label="Email"
          value={lead.email}
          href={lead.email ? `mailto:${lead.email}` : undefined}
        />
        <SummaryCell label="Assigned to" value={lead.assigneeName ?? 'Unassigned'} />
        <SummaryCell label="First response due" value={formatDate(lead.slaDueAt)} />
        <SummaryCell
          label="Attempts left"
          value={`${lead.remainingContactAttempts} of ${lead.maxContactAttempts}`}
        />
        <SummaryCell label="Source" value={titleCase(lead.source)} />
      </SummaryStrip>

      {/*
        Two columns, not three. Three equal columns left the middle one ending
        two thirds of the way down the page while the right ran past the fold —
        a screen mostly made of whitespace. The wide column is what an agent
        works in; the narrow one is state and reference.
      */}
      <div className="grid items-start gap-5 lg:grid-cols-3">
        <div className="flex flex-col gap-5 lg:col-span-2">
          <Card title={`${titleCase(lead.vertical)} request`} accent="bg-primary-400">
            {detailKeys.length === 0 ? (
              <p className="text-base text-neutral-400">
                Nothing was captured for this funnel — use Edit lead to fill it in.
              </p>
            ) : (
              <div className="grid gap-x-6 sm:grid-cols-2">
                {detailKeys.map((key: string) => (
                  <Field key={key} label={detailFieldFor(key).label} value={detailValue(key)} />
                ))}
              </div>
            )}
            <div className="mt-2 border-t border-creams-500 pt-2">
              <Field label="Message" value={lead.message} />
            </div>
          </Card>

          <Card title="Notes" accent="bg-creams-600">
            <textarea
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-base"
              rows={3}
              placeholder="Add a comment"
              value={draftComment}
              onChange={(event) => setDraftComment(event.target.value)}
            />
            {/*
              Its own row, right-aligned. The button was previously inline with
              the textarea's block flow and rendered floating in the middle of
              the card.
            */}
            <div className="mt-2 flex justify-end">
              <Button
                type={ButtonType.primary}
                disabled={draftComment.trim().length === 0}
                onClick={handleComment}
              >
                Comment
              </Button>
            </div>

            <div className="mt-3">
              {notes.length === 0 ? (
                <p className="text-base text-neutral-400">No notes yet.</p>
              ) : (
                notes.map((note: NoteEntry) => (
                  <div key={note.id} className="border-t border-creams-500 py-3">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <span className={`${CHIP} ${NOTE_LABEL[note.kind].chip}`}>
                        {NOTE_LABEL[note.kind].label}
                      </span>
                      {note.context ? (
                        <span
                          className={`${CHIP} ${resolutionStyle(
                            lead.resolutionType,
                            config?.wonResolutionTypes,
                            config?.systemOnlyResolutionTypes
                          )}`}
                        >
                          {note.context}
                        </span>
                      ) : null}
                    </div>
                    <div className="text-base text-neutral-500">{note.text}</div>
                    <div className="text-sm text-neutral-300">
                      {[note.author, formatDate(note.at)].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>

          <Card title="Contact attempts" accent="bg-creams-600">
            <p className="mb-2 text-base text-neutral-500">
              {lead.contactAttempts} of {lead.maxContactAttempts} used
              {outOfAttempts ? ' — none left' : `, ${lead.remainingContactAttempts} left`}
            </p>
            {/*
              The log, not just the counter. "2 of 3 used" does not say whether
              the last try was an hour ago or in March, which is the whole
              question when deciding to spend another one.
            */}
            {lead.contactAttemptLog.length === 0 ? (
              <p className="text-base text-neutral-400">No attempts logged.</p>
            ) : (
              lead.contactAttemptLog
                .slice()
                .sort(
                  (a: LeadContactAttemptUiModel, b: LeadContactAttemptUiModel) =>
                    b.attemptNo - a.attemptNo
                )
                .map((attempt: LeadContactAttemptUiModel) => (
                  <div
                    key={attempt.attemptNo}
                    className="border-t border-creams-500 py-2 first:border-t-0"
                  >
                    <div className="text-base text-neutral-500">
                      Attempt {attempt.attemptNo}
                      {attempt.note ? ` — ${attempt.note}` : ''}
                    </div>
                    <div className="text-sm text-neutral-300">
                      {[attempt.byName, formatDate(attempt.at)].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                ))
            )}
          </Card>

          <Card title="Timeline" accent="bg-primary-200">
            {timeline.length === 0 ? (
              <p className="text-base text-neutral-400">Nothing recorded yet.</p>
            ) : (
              timeline.map((activity: LeadActivityUiModel) => (
                <div key={activity.id} className="border-t border-gray-100 py-2 first:border-t-0">
                  <div className="text-base text-neutral-500">{activity.description}</div>
                  <div className="text-sm text-neutral-300">
                    {activity.isSystem ? 'System' : activity.actorName}
                    {' · '}
                    {formatDate(activity.createdAt)}
                  </div>
                </div>
              ))
            )}
          </Card>
        </div>

        <div className="flex flex-col gap-5">
          <Card title="Assignment" accent="bg-secondary-400">
            {/* A picker, not an id field — nobody operating this has user ids. */}
            <label className={LABEL}>Assigned to</label>
            {/*
              Team members only. A lead assigned to anyone outside the team
              tree is invisible to them — the service refuses it now, so
              offering the name would be an error waiting to happen.
            */}
            <TeamMemberPicker
              value={lead.assignedToId}
              onChange={handleAssign}
              placeholder="Search team members"
            />
            {lead.assignedAt ? (
              <p className="mt-2 text-sm text-neutral-300">
                since {formatDate(lead.assignedAt)}
                {lead.assignedByName ? ` · by ${lead.assignedByName}` : ''}
              </p>
            ) : (
              <p className="mt-2 rounded-xl bg-creams-300 px-3 py-2 text-sm text-creams-900">
                Unassigned — it stays in the queue until someone picks it up.
              </p>
            )}
          </Card>

          <Card title="Response & SLA" accent="bg-primary-300">
            <Field
              label="State"
              value={
                <span className="inline-flex">
                  <SlaBadge state={lead.slaState} />
                </span>
              }
            />
            <Field label="First response due" value={formatDate(lead.slaDueAt)} />
            <Field label="First responded" value={formatDate(lead.firstResponseAt)} />
            <Field
              label="Value category"
              value={lead.valueCategory ? titleCase(lead.valueCategory) : undefined}
            />
            <Field
              label="Quality score"
              value={
                lead.qualityScore === undefined
                  ? undefined
                  : `${lead.qualityScore}${lead.scoredAt ? ` · ${formatDate(lead.scoredAt)}` : ''}`
              }
            />
          </Card>

          <Card title="Marketing consent" accent="bg-blue">
            {/*
              PDPL keeps the evidence, not just the answer: a lead without
              consent can be worked by sales but must never be exported to a
              Meta or Google audience, and that decision is made from here.
            */}
            <Field
              label="Consented"
              value={
                <span
                  className={`${CHIP} ${
                    lead.marketingConsent
                      ? 'bg-primary-100 text-primary-800'
                      : 'bg-creams-400 text-creams-900'
                  }`}
                >
                  {lead.marketingConsent ? 'Yes' : 'No'}
                </span>
              }
            />
            <Field label="Captured at" value={formatDate(lead.consentAt)} />
            <Field
              label="Captured on"
              value={lead.consentSource ? titleCase(lead.consentSource) : undefined}
            />
          </Card>

          {/*
            Everything below is reference: read when a campaign's numbers look
            wrong or someone asks who touched a lead, not on a sales call. It
            was occupying the top right of the screen.
          */}
          <Card title="Attribution" accent="bg-blue" collapsible subtitle={attributionSummary}>
            <FieldList fields={attributionFields} emptyMessage="No attribution recorded." />
          </Card>

          <Card
            title="First touch"
            accent="bg-primary-200"
            collapsible
            subtitle={lead.firstTouchChannel ? titleCase(lead.firstTouchChannel) : 'Not recorded'}
          >
            <FieldList
              fields={firstTouchFields}
              emptyMessage="No first touch recorded — this lead arrived on its first visit, or predates first-touch tracking."
            />
          </Card>

          <Card
            title="Record"
            accent="bg-secondary-400"
            collapsible
            subtitle={lead.originLabel ?? `Created ${formatDate(lead.createdAt) ?? ''}`}
          >
            <Field label="Lead ID" value={`#${lead.shortId}`} />
            <FieldList fields={recordFields} />
          </Card>
        </div>
      </div>

      {config ? (
        <StatusModal
          show={showStatus}
          lead={lead}
          config={config}
          onClose={() => setShowStatus(false)}
          onSubmit={handleStatus}
        />
      ) : null}

      {config ? (
        <LeadFormModal
          show={showEdit}
          config={config}
          lead={lead}
          onClose={() => setShowEdit(false)}
          onSubmit={handleEdit}
        />
      ) : null}
    </div>
  );
};
